/**
 * Single source of truth for capacity-utilisation traffic-light colours.
 *
 * Every capacity surface (dashboard group cards, capacity-management day/week/
 * quarter views, group badges, tooltips, and the settings preview) maps a
 * utilisation percentage to one of three levels — normal / warning / critical —
 * using the org-configured thresholds, and renders it with the dedicated,
 * theme-aware `--capacity-*` tokens defined in `styles/index.css`. This keeps
 * the colours identical across every screen and every theme, and makes the
 * Settings "Capacity Badge Preview" show exactly what appears in the app.
 */

import { AlertOctagon, AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { msg } from '@lingui/macro';
import type { MessageDescriptor } from '@lingui/core';

export type CapacityLevel = 'empty' | 'normal' | 'warning' | 'critical';

export interface CapacityThresholds {
  /** Utilisation % at which a group is considered a warning (e.g. 100). */
  warningThreshold: number;
  /** Utilisation % at which a group is considered critical (e.g. 120). */
  criticalThreshold: number;
}

export interface CapacityLevelOptions {
  /** When the metric is 0, return `empty` instead of `normal`. */
  treatZeroAsEmpty?: boolean;
}

export interface CapacityColorClasses {
  /** The resolved level, exposed for callers that also drive labels/icons. */
  level: CapacityLevel;
  /** Solid fill for progress bars, e.g. `bg-capacity-warning`. */
  bar: string;
  /**
   * Foreground text colour on a card surface (>=4.5:1 in every theme), e.g.
   * `text-capacity-warning-on-surface`. Bound to the dedicated `-on-surface`
   * token, NOT the fill token above — the fill is too light on a light card and
   * fails WCAG 1.4.3. Do not re-bind this to `--capacity-*`.
   */
  text: string;
  /**
   * Border colour on a card surface (>=3:1, WCAG 1.4.11), e.g.
   * `border-capacity-warning-on-surface`. Same split as `text` — see above.
   */
  border: string;
  /** Soft tinted badge surface + text (readable in light and dark). */
  badge: string;
  /**
   * Soft tinted surface fill ONLY (no text/border), e.g.
   * `bg-capacity-warning/15`. For swatches and cells that set their own
   * foreground — use this instead of hand-writing the token, so a palette
   * change stays confined to this module.
   */
  surface: string;
  /**
   * The level's raw CSS colour, e.g. `hsl(var(--capacity-warning))`. For the
   * few consumers that need a colour *value* rather than a class — inline SVG
   * stroke/fill (sparklines, charts) can't take a Tailwind class. Resolves
   * through the same theme-aware token as `bar`, so it tracks light/dark.
   */
  cssVar: string;
  /** Hover surface for the soft badge. */
  badgeHover: string;
  /** Solid badge surface paired with its readable foreground. */
  badgeSolid: string;
  /**
   * Leading status glyph — the non-colour channel that keeps status readable
   * without perceiving hue (WCAG 1.4.1). `null` for `empty`, which has no
   * status to announce. Callers must null-guard before dereferencing.
   */
  icon: LucideIcon | null;
  /**
   * Short status label; `null` for `empty`. Consumers render `i18n._(label)`
   * — the descriptor keeps this module render-independent.
   */
  label: MessageDescriptor | null;
  /**
   * `true` when the level carries no status worth announcing, so consumers omit
   * the redundant status row (icon + label + percent) for it. `empty` and
   * `normal` are quiet; warning and critical are not.
   *
   * This flag governs the STATUS ROW ONLY. It must never gate colour: every
   * non-empty level renders its own traffic-light token (`bar`/`text`/`border`/
   * `surface`), so normal is green everywhere, exactly as the Settings
   * "Capacity Badge Preview" shows. `empty` already carries neutral classes of
   * its own, so a consumer can bind the colour fields unconditionally.
   */
  isQuiet: boolean;
}

/**
 * Resolve a utilisation percentage to a capacity level using the org thresholds.
 * `null`/`undefined`/`NaN` resolve to `empty` (no data / no capacity configured).
 */
export function getCapacityLevel(
  percent: number | null | undefined,
  thresholds: CapacityThresholds,
  options: CapacityLevelOptions = {},
): CapacityLevel {
  if (percent == null || Number.isNaN(percent)) return 'empty';
  if (options.treatZeroAsEmpty && percent <= 0) return 'empty';
  if (percent >= thresholds.criticalThreshold) return 'critical';
  if (percent >= thresholds.warningThreshold) return 'warning';
  return 'normal';
}

const CLASSES: Record<CapacityLevel, CapacityColorClasses> = {
  empty: {
    level: 'empty',
    bar: 'bg-muted',
    text: 'text-muted-foreground',
    border: 'border-border',
    badge: 'bg-muted text-muted-foreground border-border',
    surface: 'bg-muted/40',
    cssVar: 'hsl(var(--muted-foreground))',
    badgeHover: 'hover:bg-muted/80',
    badgeSolid: 'bg-muted text-muted-foreground',
    icon: null,
    label: null,
    isQuiet: true,
  },
  normal: {
    level: 'normal',
    bar: 'bg-capacity-normal',
    text: 'text-capacity-normal-on-surface',
    border: 'border-capacity-normal-on-surface',
    badge: 'bg-capacity-normal/15 text-capacity-normal border-capacity-normal/30',
    surface: 'bg-capacity-normal/15',
    cssVar: 'hsl(var(--capacity-normal))',
    badgeHover: 'hover:bg-capacity-normal/25',
    badgeSolid: 'bg-capacity-normal text-capacity-normal-foreground',
    icon: CheckCircle2,
    label: msg({ id: 'capacity.level.normal', message: 'Within capacity' }),
    isQuiet: true,
  },
  warning: {
    level: 'warning',
    bar: 'bg-capacity-warning',
    text: 'text-capacity-warning-on-surface',
    border: 'border-capacity-warning-on-surface',
    badge: 'bg-capacity-warning/15 text-capacity-warning border-capacity-warning/30',
    surface: 'bg-capacity-warning/15',
    cssVar: 'hsl(var(--capacity-warning))',
    badgeHover: 'hover:bg-capacity-warning/25',
    badgeSolid: 'bg-capacity-warning text-capacity-warning-foreground',
    icon: AlertTriangle,
    label: msg({ id: 'capacity.level.warning', message: 'Near capacity' }),
    isQuiet: false,
  },
  critical: {
    level: 'critical',
    bar: 'bg-capacity-critical',
    text: 'text-capacity-critical-on-surface',
    border: 'border-capacity-critical-on-surface',
    badge: 'bg-capacity-critical/15 text-capacity-critical border-capacity-critical/30',
    surface: 'bg-capacity-critical/15',
    cssVar: 'hsl(var(--capacity-critical))',
    badgeHover: 'hover:bg-capacity-critical/25',
    badgeSolid: 'bg-capacity-critical text-capacity-critical-foreground',
    icon: AlertOctagon,
    label: msg({ id: 'capacity.level.critical', message: 'Over capacity' }),
    isQuiet: false,
  },
};

/**
 * Resolve a utilisation percentage to the full set of theme-aware Tailwind
 * classes for that capacity level.
 */
export function getCapacityColor(
  percent: number | null | undefined,
  thresholds: CapacityThresholds,
  options?: CapacityLevelOptions,
): CapacityColorClasses {
  return CLASSES[getCapacityLevel(percent, thresholds, options)];
}
